import React, { useEffect, useState, useRef, memo } from "react";
import { motion, useInView, animate } from "framer-motion";
import { TbShieldCheck, TbLock } from "react-icons/tb";
import { HiArrowRight } from "react-icons/hi";
import { FaStar } from "react-icons/fa";
import Badge from "../../components/Badge/Badge";
import Card from "../../components/Card/Card";
import FeatureIcon from "../../components/FeatureIcon/FeatureIcon";
import CustomTextInput from "../../components/CustomTextInput/CustomTextInput";
import CustomButton from "../../components/CustomButton/CustomButton";
import PopupModal from "../../components/PopupModal/PopupModal";
import styles from "./BookDemo.module.css";
import AxiosInstance from "../../services/Api";
import Button from "../../components/Button/Button";
import seoConfig from "../../seo/seoConfig";
import SEO from "../../seo/SEO";

/* ============================================================
   COUNT-UP NUMBER (memoized so parent re-renders don't touch it)
============================================================ */
const CountUpNumber = memo(function CountUpNumber({
  target,
  duration = 1.6,
  decimals = 0,
  prefix = "",
  suffix = "",
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(0, target, {
      duration,
      ease: "easeOut",
      onUpdate: (latest) => {
        setValue(
          decimals > 0
            ? parseFloat(latest.toFixed(decimals))
            : Math.round(latest),
        );
      },
    });
    return () => controls.stop();
  }, [isInView, target, duration, decimals]);

  const display = decimals > 0 ? value.toFixed(decimals) : value;
  return (
    <span ref={ref}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
});

/* ============================================================
   FORM CONFIG & VALIDATION REGEXES
============================================================ */
const INITIAL_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  institution_name: "",
  address: "",
  message: "",
};

// Strict email: standard RFC-ish format
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Names: letters, spaces, hyphens, apostrophes, periods only (no numbers, no special chars)
const NAME_REGEX = /^[a-zA-Z\s'.-]+$/;

// Phone (after stripping spaces): optional leading + then only digits
const PHONE_DIGITS_REGEX = /^\+?\d+$/;

// Institution: alphanumerics + a small set of safe punctuation
const INSTITUTION_REGEX = /^[a-zA-Z0-9\s.,'&()-]+$/;

// Address: letters, numbers, common address punctuation (no @, #, $, %, etc.)
const ADDRESS_REGEX = /^[a-zA-Z0-9\s.,'/#&()-]+$/;

// Emoji detection — covers virtually all emoji unicode ranges
const EMOJI_REGEX = /\p{Extended_Pictographic}/u;

const containsEmoji = (str = "") => EMOJI_REGEX.test(str);

/* ============================================================
   VALIDATION
============================================================ */
const validateForm = (data) => {
  const errors = {};

  // ---------- First Name ----------
  const firstName = data.first_name.trim();
  if (!firstName) {
    errors.first_name = "First name is required";
  } else if (containsEmoji(data.first_name)) {
    errors.first_name = "Emojis are not allowed in first name";
  } else if (!NAME_REGEX.test(firstName)) {
    errors.first_name =
      "First name cannot contain numbers or special characters";
  } else if (firstName.length < 2) {
    errors.first_name = "First name must be at least 2 characters";
  } else if (firstName.length > 50) {
    errors.first_name = "First name must be 50 characters or less";
  }

  // ---------- Last Name ----------
  const lastName = data.last_name.trim();
  if (!lastName) {
    errors.last_name = "Last name is required";
  } else if (containsEmoji(data.last_name)) {
    errors.last_name = "Emojis are not allowed in last name";
  } else if (!NAME_REGEX.test(lastName)) {
    errors.last_name = "Last name cannot contain numbers or special characters";
  } else if (lastName.length > 50) {
    errors.last_name = "Last name must be 50 characters or less";
  }

  // ---------- Email ----------
  const email = data.email.trim();
  if (!email) {
    errors.email = "Email is required";
  } else if (containsEmoji(data.email)) {
    errors.email = "Emojis are not allowed in email";
  } else if (/\s/.test(email)) {
    errors.email = "Email cannot contain spaces";
  } else if (!EMAIL_REGEX.test(email)) {
    errors.email = "Enter a valid email address";
  } else if (email.length > 100) {
    errors.email = "Email must be 100 characters or less";
  }

  // ---------- Phone ----------
  const phoneRaw = data.phone;
  if (!phoneRaw.trim()) {
    errors.phone = "Phone number is required";
  } else if (containsEmoji(data.phone)) {
    errors.phone = "Emojis are not allowed in phone number";
  } else if (/\s/.test(phoneRaw)) {
    errors.phone = "Enter a valid phone number";
  } else if (/^[^\d+]/.test(phoneRaw)) {
    errors.phone = "Enter a valid phone number";
  } else {
    if (!PHONE_DIGITS_REGEX.test(phoneRaw)) {
      errors.phone = "Enter a valid phone number";
    } else {
      const digitsOnly = phoneRaw.replace(/^\+/, "");
      if (digitsOnly.length < 10) {
        errors.phone = "Phone number must be at least 10 digits";
      } else if (digitsOnly.length > 15) {
        errors.phone = "Phone number must be 15 digits or less";
      }
    }
  }

  // ---------- Institution Name ----------
  const institution = data.institution_name.trim();
  if (!institution) {
    errors.institution_name = "Institution name is required";
  } else if (containsEmoji(data.institution_name)) {
    errors.institution_name = "Emojis are not allowed in institution name";
  } else if (!INSTITUTION_REGEX.test(institution)) {
    errors.institution_name =
      "Institution name contains invalid special characters";
  } else if (institution.length < 2) {
    errors.institution_name = "Institution name must be at least 2 characters";
  } else if (institution.length > 100) {
    errors.institution_name = "Institution name must be 100 characters or less";
  }

  // ---------- Address ----------
  const address = data.address.trim();
  if (!address) {
    errors.address = "Address is required";
  } else if (containsEmoji(data.address)) {
    errors.address = "Emojis are not allowed in address";
  } else if (!ADDRESS_REGEX.test(address)) {
    errors.address = "Address contains invalid special characters";
  } else if (address.length < 10) {
    errors.address = "Please enter a complete address (at least 10 characters)";
  } else if (address.length > 500) {
    errors.address = "Address must be 500 characters or less";
  }

  // ---------- Message ----------
  const message = data.message.trim();
  if (!message) {
    errors.message = "Please tell us a bit about your needs";
  } else if (containsEmoji(data.message)) {
    errors.message = "Emojis are not allowed in message";
  } else if (message.length < 10) {
    errors.message = "Message should be at least 10 characters";
  } else if (message.length > 1000) {
    errors.message = "Message must be 1000 characters or less";
  }

  return errors;
};

/* ============================================================
   DEMO FORM - isolated so typing doesn't re-render parent
============================================================ */
const DemoForm = memo(function DemoForm() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [popup, setPopup] = useState({
    open: false,
    title: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);

    try {
      const cleanEmail = formData.email.trim();
      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: cleanEmail,
        address: formData.address.trim(),
        institution_name: formData.institution_name.trim(),
        country: "+91",
        phone: formData.phone.trim(),
        message: formData.message.trim(),
        created_by: cleanEmail,
      };

      const response = await AxiosInstance.post("/roll/demo-create", payload);
      const data = response.data || {};

      if (data.success) {
        setPopup({
          open: true,
          title: "Request Received",
          message:
            data.message ||
            "Demo requested successfully. Our team will reach out shortly.",
        });
        setFormData(INITIAL_FORM);
      } else {
        setPopup({
          open: true,
          title: "Submission Failed",
          message:
            data.message ||
            "We couldn't submit your request. Please try again in a moment.",
        });
      }
    } catch (err) {
      // Axios throws for non-2xx — pull server message if available
      const serverMessage = err?.response?.data?.message;

      setPopup({
        open: true,
        title: serverMessage ? "Submission Failed" : "Network Error",
        message:
          serverMessage ||
          "Unable to reach our servers. Please check your connection and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const closePopup = () => setPopup((prev) => ({ ...prev, open: false }));

  return (
    <>
      {/* 🔁 Replaced <Card> with plain <div> using the SAME class — no UI change */}
      <div className={`${styles.formCard} ${styles.formCardLg}`}>
        <h2 className={styles.formTitle}>Book Your Personalized Demo</h2>
        <p className="normalText">
          See how iFillip fits your school in a 30-minute walkthrough tailored
          to your campus, students, and workflows.
        </p>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <CustomTextInput
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="Enter your first name"
                error={errors.first_name}
                required
                autoComplete="given-name"
              />
            </div>
            <div className="col-12 col-sm-6">
              <CustomTextInput
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Enter your last name"
                error={errors.last_name}
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <CustomTextInput
            label="Institutional Email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@yourschool.edu"
            error={errors.email}
            required
            autoComplete="email"
          />

          <CustomTextInput
            label="Phone Number"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91 93447 72393"
            error={errors.phone}
            required
            autoComplete="tel"
          />

          <CustomTextInput
            label="Institution Name"
            name="institution_name"
            value={formData.institution_name}
            onChange={handleChange}
            placeholder="e.g. Greenfield Public School"
            error={errors.institution_name}
            required
            autoComplete="organization"
          />

          <CustomTextInput
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Street, city, state, pincode"
            error={errors.address}
            required
            multiline
            rows={2}
          />

          <CustomTextInput
            label="Tell us about your request?"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your school size, key needs, or anything you'd like the demo to focus on."
            error={errors.message}
            required
            multiline
            rows={4}
          />

          <Button
            type="submit"
            variant="gradient"
            size="lg"
            fullWidth
            icon={<HiArrowRight />}
            disabled={submitting}
          >
            {submitting ? "Sending..." : "Request Demo"}
          </Button>
        </form>
      </div>

      <PopupModal
        open={popup.open}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
        onOk={closePopup}
      />
    </>
  );
});

/* ============================================================
   MAIN PAGE COMPONENT
============================================================ */
const BookDemo = () => {
  return (
    <>
      <SEO {...seoConfig.bookDemo} />
      <div className={styles.page}>
        <section className={`page-container ${styles.hero}`}>
          <div className="row g-5">
            {/* LEFT: Headline, stats, testimonial */}
            <div className="col-12 col-lg-6">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Badge variant="cyan">Next Generation School Management</Badge>
                <h1 className={styles.heroTitle}>
                  Smarter Schools. <br />
                  <span className="gradientText">
                    Better Learning Outcomes.{" "}
                  </span>
                </h1>
                <p className="normalText">
                  Experience the power of AI-driven school management with
                  iFillip. From academic planning and student engagement to
                  administration and analytics, every process is connected,
                  automated, and designed to help schools operate smarter and
                  teach better.
                </p>
              </motion.div>

              {/* Stats row */}
              <div className="row g-3 mt-3">
                <div className="col-12 col-sm-5">
                  <Card variant="soft" padding="lg" className={styles.statCard}>
                    <div className={styles.bigStat}>
                      <CountUpNumber target={100} suffix="+" />
                    </div>
                    <div className={styles.statLabel}>
                      Smart Features for Modern Institutions
                    </div>
                    <p className="normalText">
                      Streamlining academics, administration, communication, and
                      student engagement through one intelligent platform.
                    </p>
                  </Card>
                </div>

                <div className="col-12 col-sm-7">
                  <Card variant="soft" padding="lg" className={styles.statCard}>
                    <FeatureIcon icon={TbShieldCheck} tint="soft" size="md" />
                    <h3 className={styles.secureTitle}>
                      Built for Secure Digital Education
                    </h3>
                    <p className="normalText">
                      iFillip ensures secure data management, role-based access,
                      and reliable cloud infrastructure designed for schools and
                      educational institutions.
                    </p>
                  </Card>
                </div>
              </div>

              {/* Trusted by */}
              {/* <motion.div
              className={styles.trusted}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className={styles.trustedHead}>
                <span className={styles.trustedBar} />
                <h3 className={styles.trustedTitle}>
                  Trusted by Forward-Thinking Institutions
                </h3>
              </div>

              <Card
                variant="default"
                padding="lg"
                className={styles.testimonialCard}
              >
                <span className={styles.quoteMark}>99</span>
                <div className={styles.testimonialTop}>
                  <img
                    src="https://i.pravatar.cc/80?img=47"
                    alt="Dr. Elena Rodriguez"
                    className={styles.avatar}
                  />
                  <div>
                    <div className={styles.tName}>Dr. Elena Rodriguez</div>
                    <div className={styles.tRole}>
                      Principal, St. Andrews International
                    </div>
                  </div>
                </div>
                <p className="normalText">
                  "We were drowning in spreadsheets and WhatsApp messages before
                  iFillip. Six months in, our teachers are reporting hours saved
                  every week, and parents finally feel in the loop. The platform
                  didn't just digitize us — it changed how we run the school."
                </p>
              </Card>

              <div className={styles.ratingRow}>
                <p className="normalText">
                  "The most intuitive school platform I've used in 20 years of
                  education."
                </p>
                <div className={styles.stars}>
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} color="#0EA5B7" size={16} />
                  ))}
                </div>
              </div>
            </motion.div> */}
            </div>

            {/* RIGHT: Form (isolated component) */}
            <div className="col-12 col-lg-6">
              <DemoForm />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BookDemo;
